/**
 * Permission Catalog — Single Source of Truth
 * ===========================================
 *
 * Every permission ID is a stable SCREAMING_SNAKE_CASE string that
 * 1. lives in server_roles.permission_ids (TEXT[]) on the server, AND
 * 2. is referenced by name in this file on the client.
 *
 * DO NOT use role names ("Administrator", "Helper", etc.) to gate
 * behavior anywhere in the app. Always look up the permission by ID.
 *
 * To add a new permission:
 *   1. Add the ID below with category + name + description + danger.
 *   2. Add it to the DEFAULT_PERMISSION_ROLES seeds in
 *      supabase/migrations/20260801000000_role_permission_engine.sql.
 *   3. Reference it via `hasPermission(userRoleIds, PERMISSION_X)`.
 *   4. No structural rewrite required — TEXT[] + TS const = forward
 *      compatible by construction.
 */

export type PermissionCategory =
  | 'general'
  | 'roles'
  | 'invites'
  | 'members'
  | 'channels'
  | 'voice'
  | 'messages'
  | 'meta';

export type PermissionDanger = 'safe' | 'caution' | 'dangerous';

export interface PermissionMeta {
  id: string;
  category: PermissionCategory;
  name: string;
  description: string;
  danger: PermissionDanger;
}

/* ---------------------------------------------------------------- *
 * Permission IDs
 * ---------------------------------------------------------------- */

export const PERMISSION_ADMINISTRATOR    = 'ADMINISTRATOR';
export const PERMISSION_MANAGE_SERVER    = 'MANAGE_SERVER';
export const PERMISSION_DELETE_SERVER    = 'DELETE_SERVER';
export const PERMISSION_VIEW_AUDIT_LOG   = 'VIEW_AUDIT_LOG';

export const PERMISSION_MANAGE_ROLES             = 'MANAGE_ROLES';
export const PERMISSION_MANAGE_COSMETIC_ROLES    = 'MANAGE_COSMETIC_ROLES';

export const PERMISSION_CREATE_INVITE   = 'CREATE_INVITE';
export const PERMISSION_MANAGE_INVITES  = 'MANAGE_INVITES';

export const PERMISSION_KICK_MEMBERS    = 'KICK_MEMBERS';
export const PERMISSION_BAN_MEMBERS     = 'BAN_MEMBERS';
export const PERMISSION_TIMEOUT_MEMBERS = 'TIMEOUT_MEMBERS';
export const PERMISSION_SERVER_MUTE     = 'SERVER_MUTE';
export const PERMISSION_SERVER_DEAFEN   = 'SERVER_DEAFEN';

export const PERMISSION_CREATE_CHANNELS    = 'CREATE_CHANNELS';
export const PERMISSION_DELETE_CHANNELS    = 'DELETE_CHANNELS';
export const PERMISSION_EDIT_CHANNELS      = 'EDIT_CHANNELS';
export const PERMISSION_MANAGE_CATEGORIES  = 'MANAGE_CATEGORIES';
export const PERMISSION_SET_VOICE_USER_LIMIT = 'SET_VOICE_USER_LIMIT';
export const PERMISSION_MANAGE_VOICE_CHANNELS = 'MANAGE_VOICE_CHANNELS';

export const PERMISSION_PIN_MESSAGES      = 'PIN_MESSAGES';
export const PERMISSION_DELETE_MESSAGES   = 'DELETE_MESSAGES';
export const PERMISSION_PURGE_MESSAGES    = 'PURGE_MESSAGES';
export const PERMISSION_MANAGE_EMOJIS     = 'MANAGE_EMOJIS';
export const PERMISSION_MANAGE_STICKERS    = 'MANAGE_STICKERS';
export const PERMISSION_SEND_MESSAGES     = 'SEND_MESSAGES';

// Members (extended)
export const PERMISSION_MANAGE_NICKNAMES  = 'MANAGE_NICKNAMES'; // edit member nicknames

// Voice / WebRTC
export const PERMISSION_CONNECT           = 'CONNECT';        // join voice channel
export const PERMISSION_STREAM            = 'STREAM';         // share camera/screen
export const PERMISSION_USE_VAD           = 'USE_VAD';        // voice activity detection (vs push-to-talk forced)
export const PERMISSION_PRIORITY_SPEAKER  = 'PRIORITY_SPEAKER';// skip listen-only when room is full

/* ---------------------------------------------------------------- *
 * Catalog entries for UI rendering (search, group, danger warning)
 * ---------------------------------------------------------------- */

export const PERMISSIONS: PermissionMeta[] = [
  // Meta
  { id: PERMISSION_ADMINISTRATOR, category: 'meta', name: 'Administrator',
    description: 'Grants every permission except Delete Server. Bypasses channel-level overrides.',
    danger: 'caution' },

  // General
  { id: PERMISSION_MANAGE_SERVER, category: 'general', name: 'Manage Server',
    description: 'Edit server name, icon, banner, and default settings.',
    danger: 'caution' },
  { id: PERMISSION_DELETE_SERVER, category: 'general', name: 'Delete Server',
    description: 'Permanently delete this entire server. Irreversible.',
    danger: 'dangerous' },
  { id: PERMISSION_VIEW_AUDIT_LOG, category: 'general', name: 'View Audit Log',
    description: 'See who took what moderation action and when.',
    danger: 'safe' },

  // Roles
  { id: PERMISSION_MANAGE_ROLES, category: 'roles', name: 'Manage Roles',
    description: 'Create, edit, reorder, and assign permission roles. Renamable.',
    danger: 'dangerous' },
  { id: PERMISSION_MANAGE_COSMETIC_ROLES, category: 'roles', name: 'Manage Cosmetic Roles',
    description: 'Create and assign cosmetic roles (badges, colors, mentions).',
    danger: 'caution' },

  // Invites
  { id: PERMISSION_CREATE_INVITE, category: 'invites', name: 'Create Invite',
    description: 'Generate invite links and add people to the server.',
    danger: 'safe' },
  { id: PERMISSION_MANAGE_INVITES, category: 'invites', name: 'Manage Invites',
    description: 'Revoke existing invites and view audience data.',
    danger: 'caution' },

  // Members
  { id: PERMISSION_KICK_MEMBERS,  category: 'members', name: 'Kick Members',
    description: 'Disconnect a member from the server. They may rejoin.',
    danger: 'caution' },
  { id: PERMISSION_BAN_MEMBERS,   category: 'members', name: 'Ban Members',
    description: 'Remove a member and block future joins (permanent or temporary).',
    danger: 'dangerous' },
  { id: PERMISSION_TIMEOUT_MEMBERS, category: 'members', name: 'Timeout Members',
    description: 'Disable a member for a configurable duration.',
    danger: 'caution' },
  { id: PERMISSION_SERVER_MUTE,   category: 'members', name: 'Server Mute',
    description: 'Prevent a member from speaking in voice channels.',
    danger: 'caution' },
  { id: PERMISSION_SERVER_DEAFEN, category: 'members', name: 'Server Deafen',
    description: 'Prevent a member from hearing others in voice channels.',
    danger: 'caution' },
  { id: PERMISSION_MANAGE_NICKNAMES, category: 'members', name: 'Manage Nicknames',
    description: 'Edit the server nickname of other members.',
    danger: 'safe' },

  // Channels
  { id: PERMISSION_CREATE_CHANNELS,   category: 'channels', name: 'Create Channels',
    description: 'Create new text and voice channels.',
    danger: 'caution' },
  { id: PERMISSION_DELETE_CHANNELS,   category: 'channels', name: 'Delete Channels',
    description: 'Delete channels and their message history.',
    danger: 'dangerous' },
  { id: PERMISSION_EDIT_CHANNELS,     category: 'channels', name: 'Edit Channels',
    description: 'Edit channel name, topic, permissions, and slow-mode.',
    danger: 'caution' },
  { id: PERMISSION_MANAGE_CATEGORIES, category: 'channels', name: 'Manage Categories',
    description: 'Create, rename, reorder, and delete channel categories.',
    danger: 'caution' },
  { id: PERMISSION_SET_VOICE_USER_LIMIT, category: 'voice', name: 'Set Voice User Limit',
    description: 'Configure how many users may join each voice channel.',
    danger: 'caution' },
  { id: PERMISSION_MANAGE_VOICE_CHANNELS, category: 'voice', name: 'Manage Voice Channels',
    description: 'Move members between voice channels and disconnect them.',
    danger: 'caution' },

  // Messages
  { id: PERMISSION_PIN_MESSAGES,    category: 'messages', name: 'Pin Messages',
    description: 'Pin messages to the top of a channel.',
    danger: 'safe' },
  { id: PERMISSION_DELETE_MESSAGES, category: 'messages', name: 'Delete Messages',
    description: 'Delete individual messages in channels you can see.',
    danger: 'caution' },
  { id: PERMISSION_PURGE_MESSAGES,  category: 'messages', name: 'Purge Messages',
    description: 'Bulk-delete messages from a channel.',
    danger: 'dangerous' },
  { id: PERMISSION_MANAGE_EMOJIS,   category: 'messages', name: 'Manage Emojis',
    description: 'Add, rename, and delete custom emojis.',
    danger: 'caution' },
  { id: PERMISSION_MANAGE_STICKERS,  category: 'messages', name: 'Manage Stickers',
    description: 'Add and delete custom stickers.',
    danger: 'caution' },
  { id: PERMISSION_SEND_MESSAGES,   category: 'messages', name: 'Send Messages',
    description: 'Send messages in text channels. Removing this effectively mutes the member.',
    danger: 'safe' },

  // Voice
  { id: PERMISSION_CONNECT,          category: 'voice', name: 'Connect',
    description: 'Join voice channels.',
    danger: 'safe' },
  { id: PERMISSION_STREAM,           category: 'voice', name: 'Stream',
    description: 'Share camera, microphone, and screen in voice channels.',
    danger: 'caution' },
  { id: PERMISSION_USE_VAD,          category: 'voice', name: 'Voice Activity',
    description: 'Use voice activity detection. If disabled, push-to-talk is forced.',
    danger: 'safe' },
  { id: PERMISSION_PRIORITY_SPEAKER, category: 'voice', name: 'Priority Speaker',
    description: 'Be heard even when the voice channel is at the listener cap.',
    danger: 'caution' },
];

export const PERMISSION_INDEX: Record<string, PermissionMeta> =
  PERMISSIONS.reduce((acc, p) => { acc[p.id] = p; return acc; }, {} as Record<string, PermissionMeta>);

/* ---------------------------------------------------------------- *
 * Default permission sets per default role
 * These mirror the SQL seed in 20260801000000_role_permission_engine.sql.
 * ---------------------------------------------------------------- */

export const DEFAULT_ROLE_PERMISSIONS = {
  /**
   * 🧙‍♂️ Server Creator — position 0, NOT deletable
   * Has every permission including MANAGE_SERVER and DELETE_SERVER.
   */
  CREATOR: [
    PERMISSION_ADMINISTRATOR,
    PERMISSION_MANAGE_SERVER,
    PERMISSION_DELETE_SERVER,
    PERMISSION_VIEW_AUDIT_LOG,
    PERMISSION_MANAGE_ROLES,
    PERMISSION_MANAGE_COSMETIC_ROLES,
    PERMISSION_CREATE_INVITE,
    PERMISSION_MANAGE_INVITES,
    PERMISSION_KICK_MEMBERS,
    PERMISSION_BAN_MEMBERS,
    PERMISSION_TIMEOUT_MEMBERS,
    PERMISSION_SERVER_MUTE,
    PERMISSION_SERVER_DEAFEN,
    PERMISSION_MANAGE_NICKNAMES,
    PERMISSION_CREATE_CHANNELS,
    PERMISSION_DELETE_CHANNELS,
    PERMISSION_EDIT_CHANNELS,
    PERMISSION_MANAGE_CATEGORIES,
    PERMISSION_SET_VOICE_USER_LIMIT,
    PERMISSION_MANAGE_VOICE_CHANNELS,
    PERMISSION_PIN_MESSAGES,
    PERMISSION_DELETE_MESSAGES,
    PERMISSION_PURGE_MESSAGES,
    PERMISSION_MANAGE_EMOJIS,
    PERMISSION_MANAGE_STICKERS,
    PERMISSION_SEND_MESSAGES,
    PERMISSION_CONNECT,
    PERMISSION_STREAM,
    PERMISSION_USE_VAD,
    PERMISSION_PRIORITY_SPEAKER,
  ] as string[],
  /**
   * 🛠 Administrator  — position 10, deletable
   * Has all admin perms EXCEPT MANAGE_SERVER and DELETE_SERVER.
   * Uses explicit set instead of ADMINISTRATOR meta-perm to prevent
   * auto-expanding to MANAGE_SERVER.
   */
  ADMINISTRATOR: [
    PERMISSION_VIEW_AUDIT_LOG,
    PERMISSION_MANAGE_ROLES,
    PERMISSION_MANAGE_COSMETIC_ROLES,
    PERMISSION_CREATE_INVITE,
    PERMISSION_MANAGE_INVITES,
    PERMISSION_KICK_MEMBERS,
    PERMISSION_BAN_MEMBERS,
    PERMISSION_TIMEOUT_MEMBERS,
    PERMISSION_SERVER_MUTE,
    PERMISSION_SERVER_DEAFEN,
    PERMISSION_MANAGE_NICKNAMES,
    PERMISSION_CREATE_CHANNELS,
    PERMISSION_DELETE_CHANNELS,
    PERMISSION_EDIT_CHANNELS,
    PERMISSION_MANAGE_CATEGORIES,
    PERMISSION_SET_VOICE_USER_LIMIT,
    PERMISSION_MANAGE_VOICE_CHANNELS,
    PERMISSION_PIN_MESSAGES,
    PERMISSION_DELETE_MESSAGES,
    PERMISSION_PURGE_MESSAGES,
    PERMISSION_MANAGE_EMOJIS,
    PERMISSION_MANAGE_STICKERS,
    PERMISSION_SEND_MESSAGES,
    PERMISSION_CONNECT,
    PERMISSION_STREAM,
    PERMISSION_USE_VAD,
    PERMISSION_PRIORITY_SPEAKER,
  ] as string[],
  /**
   * 👼 Helper — position 90, deletable
   * Support role: can mute/timeout, manage voice, create channels,
   * edit nicknames, but CANNOT kick/ban or edit/delete channels.
   */
  HELPER: [
    PERMISSION_SERVER_MUTE,
    PERMISSION_TIMEOUT_MEMBERS,
    PERMISSION_MANAGE_NICKNAMES,
    PERMISSION_CREATE_CHANNELS,
    PERMISSION_MANAGE_VOICE_CHANNELS,
    PERMISSION_SEND_MESSAGES,
    PERMISSION_CONNECT,
    PERMISSION_STREAM,
    PERMISSION_USE_VAD,
  ] as string[],
  /**
   * 🧟‍♂️ Everyone — position 999, NOT deletable
   * Baseline permissions every member gets.
   */
  EVERYONE: [
    PERMISSION_SEND_MESSAGES,
    PERMISSION_CONNECT,
    PERMISSION_USE_VAD,
    PERMISSION_STREAM,
    PERMISSION_CREATE_INVITE,
  ] as string[],
};

/* ---------------------------------------------------------------- *
 * Role-type constants
 * ---------------------------------------------------------------- */

export const ROLE_KIND_COSMETIC   = 'cosmetic';
export const ROLE_KIND_PERMISSION = 'permission';

/* ---------------------------------------------------------------- *
 * Default role IDs (stable across DB / client)
 * These match the system_role rows auto-created by the vault INSERT
 * trigger; tagged with these string IDs for client-side rendering
 * (e.g. hide delete button on @Creator).
 * ---------------------------------------------------------------- */

export const SYSTEM_ROLE_CREATOR         = 'system:creator';
export const SYSTEM_ROLE_ADMINISTRATOR   = 'system:administrator';
export const SYSTEM_ROLE_HELPER          = 'system:helper';
export const SYSTEM_ROLE_EVERYONE        = 'system:everyone';

export const DANGEROUS_PERMISSIONS: ReadonlySet<string> = new Set(
  PERMISSIONS.filter(p => p.danger === 'dangerous').map(p => p.id),
);
